import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Card,
  Text,
  Title,
  Group,
  Badge,
  Box,
  Rating,
  Stack,
  Progress,
  Center,
  BackgroundImage
} from '@mantine/core'
import {
  IconBook,
  IconClock,
  IconCheck,
  IconTrendingUp,
  IconTrendingDown,
  IconMinus
} from '@tabler/icons-react'
import { assessmentService } from '../services'
import { supabase } from '../lib/supabase'
import styles from './CourseCard.module.css'

function UserAvatar({ name, size = 40 }) {
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase()
  const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#48C9B0']
  const colorIndex = name.charCodeAt(0) % colors.length
  
  return (
    <Center
      w={size}
      h={size}
      bg={colors[colorIndex]}
      c="white"
      fw={600}
      className={styles.userAvatar}
      style={{ fontSize: size * 0.4 }}
    >
      {initials}
    </Center>
  )
}

function CourseCard({ course, user, size = 'normal' }) {
  const navigate = useNavigate()
  const [retakeInfo, setRetakeInfo] = useState(null)
  const [loadingRetakeInfo, setLoadingRetakeInfo] = useState(false)

  const levelColor = {
    'Beginner': 'gray',
    'Intermediate': 'orange',
    'Master': 'red'
  }

  const isCompleted = course.isEnrolled && course.progress === 100
  const isCompact = size === 'compact'

  // Fetch retake information if course is completed
  useEffect(() => {
    const fetchRetakeInfo = async () => {
      if (!isCompleted || !user?.email || !course.id) return

      setLoadingRetakeInfo(true)
      try {
        const { data: sessions, error } = await supabase
          .from('assessment_sessions')
          .select('attempt_number, overall_accuracy, score')
          .eq('user_email', user.email)
          .eq('course_id', course.id)
          .eq('status', 'completed')
          .order('attempt_number', { ascending: true })

        if (!error && sessions && sessions.length > 1) {
          const firstAttempt = sessions[0]
          const latestAttempt = sessions[sessions.length - 1]
          const improvement = latestAttempt.overall_accuracy - firstAttempt.overall_accuracy

          setRetakeInfo({
            totalAttempts: sessions.length,
            currentAttempt: latestAttempt.attempt_number,
            firstAccuracy: firstAttempt.overall_accuracy,
            latestAccuracy: latestAttempt.overall_accuracy,
            improvement: improvement,
            hasImproved: improvement > 0,
            hasRegressed: improvement < 0
          })
        }
      } catch (error) {
        if (import.meta.env.DEV) console.error('Error fetching retake info:', error)
      } finally {
        setLoadingRetakeInfo(false)
      }
    }

    fetchRetakeInfo()
  }, [isCompleted, user?.email, course.id])

  return (
    <Card 
      shadow="sm" 
      radius="md" 
      padding={isCompact ? "md" : "lg"} 
      className={`${styles.card} ${isCompleted ? styles.cardCompleted : ''}`}
      onClick={async () => {
        // CRITICAL FIX (GAP 3.3): Check for active sessions BEFORE checking isCompleted
        // This ensures users can resume in-progress retakes instead of being sent to old results

        // Check if user has already started this course
        const acknowledgmentKey = `course_acknowledged_${course.id}_${user?.email}`
        const hasAcknowledged = localStorage.getItem(acknowledgmentKey) === 'true'
        
        if (hasAcknowledged && user?.email) {
          // Check for existing sessions (both active and completed)
          const { data: sessions } = await assessmentService.getUserAssessments(user.email, course.id)

          // Find active (in_progress) session - exclude completed and abandoned
          const activeSession = sessions?.find(s => s.status === 'in_progress')

          // If no active session but has valid previous sessions (not abandoned), go to pre-process
          const validSessions = sessions?.filter(s => s.status !== 'abandoned') || []
          if (!activeSession && validSessions.length > 0) {
            // Has previous completed session, go to pre-process page for retake option
            navigate(`/courses/${course.id}`)
            return
          }

          if (activeSession) {
            // Resume to appropriate place based on phase
            localStorage.setItem('currentAssessmentSession', activeSession.id)
            
            switch (activeSession.phase) {
              case 'initial':
                // Resume to next question
                const resumeAt = activeSession.last_question_index 
                  ? activeSession.last_question_index + 1 
                  : 1
                navigate(`/assessment/${course.id}/question/${resumeAt}`)
                break
              case 'review':
                // Go back to review page
                navigate(`/assessment/${course.id}/review`)
                break
              case 'learning':
                // Go to learning page
                navigate(`/assessment/${course.id}/learn`)
                break
              case 'reassessment':
                // Calculate which reassessment question to resume at
                // based on failed questions vs correctly answered ghost questions
                const { data: failedInCycle1 } = await supabase
                  .from('question_responses')
                  .select('question_id')
                  .eq('session_id', activeSession.id)
                  .eq('cycle_number', 1)
                  .eq('is_correct', false)

                const { data: answeredCorrectlyInReassessment } = await supabase
                  .from('question_responses')
                  .select('question_id')
                  .eq('session_id', activeSession.id)
                  .gte('cycle_number', 2)
                  .eq('is_correct', true)

                const totalFailed = failedInCycle1?.length || 0
                const totalCorrect = answeredCorrectlyInReassessment?.length || 0

                // CRITICAL FIX: Server-side reassessment completion check
                // Only complete if ALL failed questions have been answered CORRECTLY
                if (totalCorrect >= totalFailed && totalFailed > 0) {
                  if (import.meta.env.DEV) console.log('Server-side completion check: Reassessment complete, marking session as complete')

                  try {
                    await assessmentService.completeSession(activeSession.id, course.id)

                    // DEFENSIVE FIX: Verify enrollment was updated, force update if not
                    // This prevents infinite loop where enrollment stays at old progress
                    const { data: enrollment } = await supabase
                      .from('enrollments')
                      .select('status, progress')
                      .eq('user_email', user.email)
                      .eq('course_id', course.id)
                      .single()

                    if (enrollment && enrollment.status !== 'completed') {
                      if (import.meta.env.DEV) console.warn('[CourseCard] Enrollment not completed after completeSession, forcing update')
                      await supabase
                        .from('enrollments')
                        .update({
                          status: 'completed',
                          progress: 100,
                          completed_at: new Date().toISOString()
                        })
                        .eq('user_email', user.email)
                        .eq('course_id', course.id)
                    }
                  } catch (error) {
                    if (import.meta.env.DEV) console.error('[CourseCard] Error completing session:', error)
                  }

                  navigate(`/assessment/${course.id}/complete`)
                  return
                }

                // Calculate resume position based on correct answers
                // User needs to retry questions until all are correct
                const resumeAtQuestion = Math.max(1, Math.min(totalFailed, totalCorrect + 1))

                // Log warning if data integrity issue detected
                if (totalCorrect > totalFailed) {
                  if (import.meta.env.DEV) console.error('Data integrity issue: More correct reassessment answers than failed questions', {
                    courseId: course.id,
                    sessionId: activeSession.id,
                    totalFailed,
                    totalCorrect
                  })
                }

                if (import.meta.env.DEV) console.log(`Reassessment resume (CourseCard): ${totalCorrect} of ${totalFailed} correct, resuming at question ${resumeAtQuestion}`)
                navigate(`/assessment/${course.id}/reassess/${resumeAtQuestion}`)
                break
              default:
                // Fallback to pre-process
                navigate(`/courses/${course.id}`)
            }
            return
          }
        }

        // CRITICAL FIX (GAP 3.3): Check isCompleted AFTER checking for active sessions
        // If no active session and course is completed, show results page
        if (isCompleted) {
          navigate(`/assessment/${course.id}/complete`)
          return
        }

        // First time or no incomplete session - go to pre-process
        navigate(`/courses/${course.id}`)
      }}
    >
      <Card.Section>
        <BackgroundImage
          src={course.thumbnail || `https://images.unsplash.com/photo-1516116216624-53e697fedbea?w=400`}
          h={isCompact ? 120 : 180}
          pos="relative"
        >
          <Badge
            color={levelColor[course.level]}
            variant="filled"
            style={{
              position: 'absolute',
              top: isCompact ? 8 : 12,
              left: isCompact ? 8 : 12
            }}
            size={isCompact ? "xs" : "md"}
          >
            {course.level}
          </Badge>
          {isCompleted && !retakeInfo && (
            <Badge
              color="green"
              variant="filled"
              style={{
                position: 'absolute',
                top: isCompact ? 8 : 12,
                right: isCompact ? 8 : 12
              }}
              leftSection={<IconCheck size={isCompact ? 12 : 14} />}
              size={isCompact ? "xs" : "md"}
            >
              Completed
            </Badge>
          )}
          {isCompleted && retakeInfo && (
            <Group
              gap={4}
              style={{
                position: 'absolute',
                top: isCompact ? 8 : 12,
                right: isCompact ? 8 : 12
              }}
            >
              <Badge
                color={retakeInfo.hasImproved ? 'green' : retakeInfo.hasRegressed ? 'red' : 'gray'}
                variant="filled"
                size={isCompact ? "xs" : "md"}
                leftSection={
                  retakeInfo.hasImproved ? <IconTrendingUp size={isCompact ? 10 : 12} /> :
                  retakeInfo.hasRegressed ? <IconTrendingDown size={isCompact ? 10 : 12} /> :
                  <IconMinus size={isCompact ? 10 : 12} />
                }
              >
                Attempt {retakeInfo.currentAttempt} of {retakeInfo.totalAttempts}
              </Badge>
            </Group>
          )}
          {!isCompleted && course.isMandatory && (
            <Badge
              color="red"
              variant="filled"
              style={{
                position: 'absolute',
                top: isCompact ? 8 : 12,
                right: isCompact ? 8 : 12
              }}
              size={isCompact ? "xs" : "md"}
            >
              Mandatory
            </Badge>
          )}
        </BackgroundImage>
      </Card.Section>

      <Stack gap={isCompact ? "xs" : "xs"} style={{ flex: 1 }} mt={isCompact ? "sm" : "md"}>
        <Title order={isCompact ? 6 : 5} lineClamp={2} style={{ flex: 1 }}>
          {course.title}
        </Title>

        <Box>
          {!isCompact && (
            <Group gap="xs" mb="sm">
              <UserAvatar name={course.instructor} size={24} />
              <Text size="sm" c="dimmed">{course.instructor}</Text>
            </Group>
          )}

          <Group gap={isCompact ? "sm" : "xl"} mb="xs">
            <Group gap={4}>
              <IconBook size={14} color="gray" />
              <Text size={isCompact ? "xs" : "sm"} c="dimmed">{course.modules} {course.modules === 1 ? 'Question' : 'Questions'}</Text>
            </Group>
            <Group gap={4}>
              <IconClock size={14} color="gray" />
              <Text size={isCompact ? "xs" : "sm"} c="dimmed">{course.duration}</Text>
            </Group>
          </Group>

          {!isCompact && course.rating > 0 && (
            <Group align="center" gap="xs">
              <Rating value={course.rating} fractions={2} readOnly size="sm" />
              <Text size="sm" fw={500}>{course.rating}</Text>
            </Group>
          )}

          <Box mt={isCompact ? "sm" : "md"}>
            {!isCompact && <Text size="xs" c="dimmed" mb={4}>Progress</Text>}
            <Progress value={course.progress || 0} size="sm" color="blue" />
            <Text size="xs" c="dimmed" mt={4}>{course.progress || 0}% Complete</Text>

            {/* Show retake improvement/regression info */}
            {retakeInfo && !isCompact && (
              <Group gap="xs" mt="xs">
                <Text size="xs" c="dimmed">
                  First: {Math.round(retakeInfo.firstAccuracy)}%
                </Text>
                <Text size="xs" c="dimmed">→</Text>
                <Text size="xs" c="dimmed">
                  Latest: {Math.round(retakeInfo.latestAccuracy)}%
                </Text>
                <Badge
                  size="xs"
                  variant="light"
                  color={retakeInfo.hasImproved ? 'green' : retakeInfo.hasRegressed ? 'red' : 'gray'}
                >
                  {retakeInfo.improvement > 0 ? '+' : ''}{Math.round(retakeInfo.improvement)}%
                </Badge>
              </Group>
            )}
          </Box>
        </Box>
      </Stack>
    </Card>
  )
}

export default CourseCard